/**
 * MonthView — 6x7 day grid (11E.7).
 *
 * Month is a *density* problem, not a layout one: a busy Saturday can hold
 * eight sessions in a cell ~90px tall. Rather than scroll inside a cell
 * (which hides content behind an interaction most operators never try) we
 * render the first `MAX_CHIPS_PER_DAY` and a "+N more" affordance that
 * jumps to the Day view for that date — where everything fits.
 *
 * Blocks (holidays / blocked time) render as a thin banner above the
 * chips, so a holiday reads at a glance without occupying a chip slot.
 */

import React from 'react';

import { SessionChip } from '@features/calendar/components/SessionChip';
import { CALENDAR_UI } from '@features/calendar/config/ui';
import { fmtIsoDate, sameWallClockDay } from '@features/calendar/utils/tz';
import type { CalendarBlockResponse } from '@features/calendar/api/blocks';
import type { SessionResponse } from '@features/calendar/api/sessions';

export interface MonthViewProps {
  /** 42 tenant-tz wall-clock dates, Monday-first. */
  gridDates: Date[];
  /** Month the grid is "about" — cells outside it are dimmed. */
  currentMonth0: number;
  todayInTz: Date;
  tenantTz: string;
  /** ISO date (`YYYY-MM-DD`) → sessions starting that day. */
  sessionsByDay: Map<string, SessionResponse[]>;
  /** ISO date → blocks overlapping that day. */
  blocksByDay: Map<string, CalendarBlockResponse[]>;
  subjectNames?: Record<string, string>;
  studentNames?: Record<string, string>;
  onSessionClick: (session: SessionResponse) => void;
  onShowMore: (date: Date) => void;
}

const MAX_CHIPS_PER_DAY = 3;

export const MonthView: React.FC<MonthViewProps> = ({
  gridDates,
  currentMonth0,
  todayInTz,
  tenantTz,
  sessionsByDay,
  blocksByDay,
  subjectNames,
  studentNames,
  onSessionClick,
  onShowMore,
}) => (
  <div
    data-testid="candeza-calendar-month-grid"
    style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(7, 1fr)',
      gap: 1,
      background: '#e2e8f0',
      border: '1px solid #e2e8f0',
      borderRadius: 8,
      overflow: 'hidden',
    }}
  >
    {CALENDAR_UI.WEEKDAY_LABELS.map((label) => (
      <div
        key={label}
        style={{
          background: '#f8fafc',
          padding: 8,
          textAlign: 'center',
          fontSize: 12,
          fontWeight: 600,
          color: '#64748b',
        }}
      >
        {label}
      </div>
    ))}

    {gridDates.map((d) => {
      const iso = fmtIsoDate(d);
      const daySessions = sessionsByDay.get(iso) ?? [];
      const dayBlocks = blocksByDay.get(iso) ?? [];
      const isCurrentMonth = d.getMonth() === currentMonth0;
      const isToday = sameWallClockDay(d, todayInTz);
      const overflow = daySessions.length - MAX_CHIPS_PER_DAY;

      return (
        <div
          key={iso}
          data-testid={`candeza-calendar-month-cell-${iso}`}
          style={{
            background: isCurrentMonth ? '#ffffff' : '#fafafa',
            minHeight: 104,
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            opacity: isCurrentMonth ? 1 : 0.55,
          }}
        >
          <div
            style={{
              fontSize: 12,
              fontWeight: isToday ? 700 : 500,
              color: isToday ? '#4338ca' : '#334155',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <span
              style={
                isToday
                  ? {
                      background: '#e0e7ff',
                      borderRadius: 999,
                      padding: '1px 7px',
                    }
                  : undefined
              }
            >
              {d.getDate()}
            </span>
          </div>

          {dayBlocks.map((b) => (
            <div
              key={b.id}
              data-testid={`candeza-calendar-block-${b.id}`}
              data-block-kind={b.kind}
              title={b.title}
              style={{
                fontSize: 9,
                padding: '1px 4px',
                borderRadius: 3,
                background: b.kind === 'holiday' ? '#fef9c3' : '#f1f5f9',
                color: b.kind === 'holiday' ? '#854d0e' : '#475569',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {b.kind === 'holiday' ? '★ ' : '▦ '}
              {b.title}
            </div>
          ))}

          {daySessions.slice(0, MAX_CHIPS_PER_DAY).map((s) => (
            <SessionChip
              key={s.id}
              session={s}
              tenantTz={tenantTz}
              subjectNames={subjectNames}
              studentNames={studentNames}
              density="compact"
              draggable={false}
              onClick={(e) => {
                e.stopPropagation();
                onSessionClick(s);
              }}
            />
          ))}

          {overflow > 0 && (
            <button
              type="button"
              data-testid={`candeza-calendar-month-more-${iso}`}
              onClick={() => onShowMore(d)}
              style={{
                border: 'none',
                background: 'transparent',
                color: '#4f46e5',
                fontSize: 10,
                textAlign: 'left',
                cursor: 'pointer',
                padding: '1px 4px',
              }}
            >
              +{overflow} more
            </button>
          )}
        </div>
      );
    })}
  </div>
);
